import type { CmmdForgeRuntimeApiConfig } from "../environments/primary/cmmdForgeRuntimeApi";
import type { CloudProjectCatalog } from "../environments/primary/cloudProjectCatalog";
import { isCloudBearerAuthFailure } from "../lib/cloudAuthFailure";
import {
  CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
  resolveCmmdStartupAuthFailureDecision,
} from "../startup/startupReadiness";
import {
  createCloudCatalogAuthHeaders,
  consumeSseFrames,
  prepareSseReader,
  requestCloudProjectCatalog,
  resolveCloudCatalogEventsUrl,
} from "./cmmdForgeRuntimeListTransportInternals";

export const CLOUD_PROJECT_CATALOG_LIST_URL = "/api/cmmd/forge-cloud-projects";
export const CLOUD_PROJECT_CATALOG_EVENTS_URL = "/api/cmmd/forge-cloud-projects/events";
export const CMMD_CLOUD_CATALOG_REQUEST_TIMEOUT_MS = 15_000;

const POLL_INTERVAL_MS = 30_000;
const SSE_RECONNECT_BASE_MS = 2_000;
const SSE_RECONNECT_CAP_MS = 30_000;
const REFRESH_COALESCE_MS = 200;

function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer !== null) clearTimeout(timer);
  return null;
}

export type CmmdCloudCatalogTransportEvent =
  | { readonly kind: "loading" }
  | { readonly kind: "catalog"; readonly catalog: CloudProjectCatalog; readonly fetchedAt: string }
  | { readonly kind: "failed"; readonly message: string }
  | { readonly kind: "auth-refreshing" }
  | { readonly kind: "auth-required"; readonly message: string };

export interface CmmdCloudCatalogTransport {
  readonly initialize: () => void;
  readonly refresh: () => void;
  readonly teardown: () => void;
}

interface CmmdCloudCatalogTransportOptions {
  readonly config: CmmdForgeRuntimeApiConfig;
  readonly onEvent: (event: CmmdCloudCatalogTransportEvent) => void;
}

export function createCmmdCloudCatalogTransport({
  config,
  onEvent,
}: CmmdCloudCatalogTransportOptions): CmmdCloudCatalogTransport {
  let initialized = false;
  let stopped = false;
  let usingSseFallback = false;
  let authRequired = false;
  let awaitingAuthRetry = false;
  let authFailureAttempt = 0;
  let authRetrySource: "list" | "sse" | null = null;
  let sseAttempt = 0;
  let sseAbort: AbortController | null = null;
  let listRequest: { readonly controller: AbortController; readonly generation: number } | null =
    null;
  let listGeneration = 0;
  let pollTimer: ReturnType<typeof setTimeout> | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let authRetryTimer: ReturnType<typeof setTimeout> | null = null;
  let sseReconnectTimer: ReturnType<typeof setTimeout> | null = null;

  function clearBackgroundTimers(): void {
    pollTimer = clearTimer(pollTimer);
    sseReconnectTimer = clearTimer(sseReconnectTimer);
  }

  function invalidateListRequest(): void {
    listGeneration += 1;
    listRequest?.controller.abort();
    listRequest = null;
  }

  function ownsListRequest(controller: AbortController, generation: number): boolean {
    return (
      listRequest?.controller === controller &&
      listRequest.generation === generation &&
      listGeneration === generation
    );
  }

  function ownsSseRequest(controller: AbortController): boolean {
    return sseAbort === controller;
  }

  function stopTransport(): void {
    invalidateListRequest();
    sseAbort?.abort();
    sseAbort = null;
    clearBackgroundTimers();
    authRetryTimer = clearTimer(authRetryTimer);
  }

  function isActive(): boolean {
    return !stopped && !authRequired;
  }

  function emitCatalog(catalog: CloudProjectCatalog): void {
    onEvent({ kind: "catalog", catalog, fetchedAt: new Date().toISOString() });
  }

  function raiseAuthRequired(message: string): void {
    if (authRequired || stopped) return;
    authRequired = true;
    authRetrySource = null;
    stopTransport();
    onEvent({ kind: "auth-required", message });
  }

  function handleAuthFailure(message: string, source: "list" | "sse"): void {
    if (stopped || authRequired) return;
    authFailureAttempt += 1;
    authRetrySource = source;
    const decision = resolveCmmdStartupAuthFailureDecision({
      failedAttempt: authFailureAttempt,
      retryDelayMs: CMMD_STARTUP_AUTH_RETRY_DELAY_MS,
    });
    if (decision.kind === "terminal") {
      raiseAuthRequired(message);
      return;
    }
    sseAbort?.abort();
    sseAbort = null;
    sseReconnectTimer = clearTimer(sseReconnectTimer);
    onEvent({ kind: "auth-refreshing" });
    awaitingAuthRetry = true;
    authRetryTimer = clearTimer(authRetryTimer);
    authRetryTimer = setTimeout(() => {
      authRetryTimer = null;
      awaitingAuthRetry = false;
      void listAndResume();
    }, decision.delayMs);
  }

  async function listCatalog(): Promise<boolean> {
    if (!isActive()) return false;
    const controller = new AbortController();
    const generation = ++listGeneration;
    listRequest = { controller, generation };
    onEvent({ kind: "loading" });
    try {
      const result = await requestCloudProjectCatalog(
        config,
        controller,
        CMMD_CLOUD_CATALOG_REQUEST_TIMEOUT_MS,
      );
      return handleListCatalogResult(result, controller, generation);
    } finally {
      if (ownsListRequest(controller, generation)) listRequest = null;
    }
  }

  function handleListCatalogResult(
    result: Awaited<ReturnType<typeof requestCloudProjectCatalog>>,
    controller: AbortController,
    generation: number,
  ): boolean {
    if (!isActive() || !ownsListRequest(controller, generation)) return false;
    if (result.kind === "catalog") return publishListCatalog(result.catalog);
    return result.kind === "timed-out" ? reportListTimeout() : reportListFailure(result.error);
  }

  function publishListCatalog(catalog: CloudProjectCatalog): boolean {
    if (authRetrySource !== "sse") {
      authFailureAttempt = 0;
      authRetrySource = null;
    }
    awaitingAuthRetry = false;
    emitCatalog(catalog);
    return true;
  }

  function reportListTimeout(): boolean {
    onEvent({
      kind: "failed",
      message: "Cloud connection refresh timed out. Showing last known connections.",
    });
    return !authRequired;
  }

  function reportListFailure(error: unknown): boolean {
    const message = error instanceof Error ? error.message : "Failed to load cloud projects";
    const requiresAuthentication =
      isCloudBearerAuthFailure(error) ||
      isCmmdAuthError(error) ||
      /authentication required/i.test(message);
    if (requiresAuthentication) handleAuthFailure(message, "list");
    else onEvent({ kind: "failed", message });
    return !authRequired;
  }

  function schedulePoll(): void {
    if (!isActive()) return;
    pollTimer = clearTimer(pollTimer);
    pollTimer = setTimeout(async () => {
      await listCatalog();
      if (isActive()) schedulePoll();
    }, POLL_INTERVAL_MS);
  }

  function resumeBackgroundUpdates(): void {
    if (!isActive() || awaitingAuthRetry) return;
    if (usingSseFallback) schedulePoll();
    else void startSse(sseAttempt);
  }

  function scheduleSseReconnect(attempt: number): void {
    sseAttempt = attempt + 1;
    const delay = Math.min(SSE_RECONNECT_BASE_MS * 2 ** attempt, SSE_RECONNECT_CAP_MS);
    sseReconnectTimer = clearTimer(sseReconnectTimer);
    sseReconnectTimer = setTimeout(() => {
      sseReconnectTimer = null;
      void startSse(sseAttempt);
    }, delay);
  }

  function handleSseCatalog(catalog: CloudProjectCatalog): void {
    authFailureAttempt = 0;
    authRetrySource = null;
    sseAttempt = 0;
    emitCatalog(catalog);
  }

  async function startSse(attempt: number): Promise<void> {
    if (!isActive() || sseAbort !== null) return;
    sseAbort = new AbortController();
    const controller = sseAbort;
    const url =
      config.eventsUrl ??
      resolveCloudCatalogEventsUrl(config.cmmdHttpBaseUrl, "/api/forge/context/runtimes/events");
    const headers = config.eventsUrl
      ? ({ Accept: "text/event-stream" } as Record<string, string>)
      : createCloudCatalogAuthHeaders(config);
    try {
      const response = await fetch(url, {
        headers,
        signal: controller.signal,
        credentials: "include",
      });
      await handleSseResponse(response, controller, attempt);
    } catch {
      fallbackFromSseRequestFailure(controller);
    }
  }

  async function handleSseResponse(
    response: Response,
    controller: AbortController,
    attempt: number,
  ): Promise<void> {
    if (!isCurrentSseRequest(controller)) return;
    const sseReader = await prepareSseReader(response);
    if (!isCurrentSseRequest(controller)) return;
    if (typeof sseReader === "string") {
      applySseSetup(sseReader);
      return;
    }
    await consumeSseFrames({
      reader: sseReader,
      signal: controller.signal,
      onAuthRequired: () => handleSseAuthRequiredIfCurrent(controller),
      onCatalog: (catalog) => publishSseCatalogIfCurrent(controller, catalog),
    });
    scheduleSseReconnectIfCurrent(controller, attempt);
  }

  function isCurrentSseRequest(controller: AbortController): boolean {
    return isActive() && ownsSseRequest(controller);
  }

  function handleSseAuthRequiredIfCurrent(controller: AbortController): void {
    if (!isCurrentSseRequest(controller)) return;
    handleAuthFailure("CMMD authentication required. Sign in again to continue.", "sse");
  }

  function publishSseCatalogIfCurrent(
    controller: AbortController,
    catalog: CloudProjectCatalog,
  ): void {
    if (!isCurrentSseRequest(controller)) return;
    handleSseCatalog(catalog);
  }

  function applySseSetup(setup: "auth-required" | "fallback"): void {
    sseAbort = null;
    if (setup === "auth-required") {
      handleAuthFailure("CMMD authentication required. Sign in again to continue.", "sse");
      return;
    }
    usingSseFallback = true;
    schedulePoll();
  }

  function fallbackFromSseRequestFailure(controller: AbortController): void {
    if (controller.signal.aborted || !isCurrentSseRequest(controller)) return;
    sseAbort = null;
    usingSseFallback = true;
    schedulePoll();
  }

  function scheduleSseReconnectIfCurrent(controller: AbortController, attempt: number): void {
    if (controller.signal.aborted || !isCurrentSseRequest(controller)) return;
    sseAbort = null;
    scheduleSseReconnect(attempt);
  }

  function listAndResume(): Promise<void> {
    return listCatalog().then((canResume) => {
      if (canResume && isActive()) resumeBackgroundUpdates();
    });
  }

  return {
    initialize(): void {
      if (initialized || stopped) return;
      initialized = true;
      void listAndResume();
    },
    refresh(): void {
      if (stopped) return;
      invalidateListRequest();
      refreshTimer = clearTimer(refreshTimer);
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        authRequired = false;
        awaitingAuthRetry = false;
        authFailureAttempt = 0;
        authRetrySource = null;
        stopTransport();
        void listAndResume();
      }, REFRESH_COALESCE_MS);
    },
    teardown(): void {
      stopped = true;
      refreshTimer = clearTimer(refreshTimer);
      stopTransport();
    },
  };
}

function isCmmdAuthError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "kind" in error &&
    (error as { readonly kind: unknown }).kind === "auth"
  );
}
